import { useParams } from "react-router-dom";
import { gql } from "graphql-tag";
import React from "react";
import { useMutation, useQuery } from "@apollo/client";
import { LoadingSpinner, ServerError } from "components/graphql-views";
import { AdminHeading } from "../shared/admin-form";
import { useAdminSubmit } from "../shared/use-admin-submit";
import OrganizationForm, { formSchema, OrgForm } from "./form";

const ORGANIZATION = gql`
  query getOrg($id: ID!) {
    organization(id: $id) {
      id
      name
      type
      email
      bccEmail
      phone
      address1
      address2
      city
      state
      zip
      invoicedCustomer {
        invoicedId
        active
      }
    }
  }
`;

const UPDATE_ORGANIZATION = gql`
  mutation updateOrg($id: ID!, $data: OrganizationInput!) {
    updateOrganization(id: $id, data: $data) {
      id
      name
      type
      email
      bccEmail
      phone
      address1
      address2
      city
      state
      zip
      invoicedCustomer {
        invoicedId
        active
      }
    }
  }
`;

type QueryData = {
  organization: {
    id: string;
    type: string;
    name: string;
    phone: string;
    phoneExt: string;
    email: string;
    bccEmail: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
  };
}

type Props = {
  id?: string;
}

type ResData = {
  organization: {
    id: string;
  };
}

const UpdateOrganization: React.FC<Props> = () => {
  const { id: orgId } = useParams<{ id: string }>();
  const { sending, submit } = useAdminSubmit("/dash/admin/organizations");
  const { loading, error, data } = useQuery<QueryData>(ORGANIZATION, { variables: { id: orgId } });
  const [updateOrg] = useMutation<ResData>(UPDATE_ORGANIZATION);

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return null;

  const submitForm = (formData: OrgForm) =>
    submit(() => updateOrg({ variables: { id: orgId, data: formData } }));

  // casting strips __typename from the apollo response data
  // later this will also take null data and make it an empty string
  // defaulting type to property
  const organization = formSchema.cast({ ...data.organization, type: "property" });

  return (
    <div>
      <AdminHeading>Update an Organization</AdminHeading>
      <OrganizationForm
        formType="update"
        // @ts-ignore
        initialData={organization}
        sending={sending}
        handleSubmit={submitForm}
      />
    </div>);
};

export default UpdateOrganization;
